import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './consent-theme.css';
import * as CookieConsent from 'vanilla-cookieconsent';
import settings from './settings';

/**
 * Consent-Management auf Basis von vanilla-cookieconsent (MIT, self-hosted) —
 * Ersatz für Cookiebot. Lädt nichts von Dritt-CDNs.
 *
 * Bildet die frühere Cookiebot-API nach, damit Library.jsx (YouTube/Deezer-
 * Gating) unverändert weiterläuft:
 *   - window.Cookiebot.consent.{statistics,marketing} + .renew()
 *   - Events CookiebotOnAccept / CookiebotOnDecline / CookieConsent
 *
 * Kategorien: statistics = Google Analytics + Deezer, marketing = YouTube.
 */

let gaLoaded = false;
function loadGoogleAnalytics() {
  if (gaLoaded || !settings.googleAnalyticsId) return;
  gaLoaded = true;

  const gtagScript = document.createElement('script');
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${settings.googleAnalyticsId}`;
  document.head.appendChild(gtagScript);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', settings.googleAnalyticsId, { anonymize_ip: true });
}

function syncConsentState() {
  const statistics = CookieConsent.acceptedCategory('analytics');
  const marketing = CookieConsent.acceptedCategory('marketing');
  const consent = { necessary: true, preferences: true, statistics, marketing };

  window.Cookiebot = {
    consent,
    renew: () => CookieConsent.showPreferences(),
  };

  window.dispatchEvent(new CustomEvent('CookieConsent', { detail: { consent } }));
  window.dispatchEvent(new Event(statistics || marketing ? 'CookiebotOnAccept' : 'CookiebotOnDecline'));

  if (statistics) loadGoogleAnalytics();
}

export function initConsent() {
  CookieConsent.run({
    guiOptions: {
      consentModal: { layout: 'box', position: 'bottom right', equalWeightButtons: true },
      preferencesModal: { layout: 'box', equalWeightButtons: true },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: {},
      marketing: {},
    },
    language: {
      default: 'de',
      autoDetect: 'browser',
      translations: {
        de: {
          consentModal: {
            title: 'Wir respektieren deine Privatsphäre',
            description:
              'Notwendige Cookies sind für den Betrieb der Website erforderlich. Mit deiner Einwilligung nutzen wir zusätzlich Statistik (Google Analytics, eingebettete Deezer-Inhalte) und Marketing (YouTube-Videos). Mehr in der <a href="/privacy">Datenschutzerklärung</a>.',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Alle ablehnen',
            showPreferencesBtn: 'Einstellungen',
          },
          preferencesModal: {
            title: 'Cookie-Einstellungen',
            acceptAllBtn: 'Alle akzeptieren',
            acceptNecessaryBtn: 'Alle ablehnen',
            savePreferencesBtn: 'Auswahl speichern',
            closeIconLabel: 'Schließen',
            sections: [
              {
                title: 'Technisch notwendig',
                description: 'Für den Betrieb der Website erforderlich. Immer aktiv.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Statistik',
                description: 'Google Analytics (anonymisierte Reichweitenmessung, IP-Anonymisierung aktiv) und eingebettete Deezer-Inhalte.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketing',
                description: 'Eingebettete YouTube-Videos (youtube-nocookie).',
                linkedCategory: 'marketing',
              },
            ],
          },
        },
        en: {
          consentModal: {
            title: 'We value your privacy',
            description:
              'Necessary cookies are required to run the website. With your consent we also use statistics (Google Analytics, embedded Deezer content) and marketing (YouTube videos). See our <a href="/privacy">privacy policy</a>.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            showPreferencesBtn: 'Manage preferences',
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'Strictly necessary',
                description: 'Required to run the website. Always active.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Statistics',
                description: 'Google Analytics (anonymised analytics, IP anonymisation enabled) and embedded Deezer content.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketing',
                description: 'Embedded YouTube videos (youtube-nocookie).',
                linkedCategory: 'marketing',
              },
            ],
          },
        },
        fr: {
          consentModal: {
            title: 'Nous respectons votre vie privée',
            description:
              'Les cookies nécessaires sont requis pour le fonctionnement du site. Avec votre consentement, nous utilisons aussi des statistiques (Google Analytics, contenus Deezer intégrés) et du marketing (vidéos YouTube). Voir la <a href="/privacy">politique de confidentialité</a>.',
            acceptAllBtn: 'Tout accepter',
            acceptNecessaryBtn: 'Tout refuser',
            showPreferencesBtn: 'Préférences',
          },
          preferencesModal: {
            title: 'Préférences de cookies',
            acceptAllBtn: 'Tout accepter',
            acceptNecessaryBtn: 'Tout refuser',
            savePreferencesBtn: 'Enregistrer',
            closeIconLabel: 'Fermer',
            sections: [
              {
                title: 'Strictement nécessaires',
                description: 'Nécessaires au fonctionnement du site. Toujours actifs.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Statistiques',
                description: 'Google Analytics (mesure anonymisée, anonymisation IP activée) et contenus Deezer intégrés.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketing',
                description: 'Vidéos YouTube intégrées (youtube-nocookie).',
                linkedCategory: 'marketing',
              },
            ],
          },
        },
      },
    },
    onFirstConsent: syncConsentState,
    onConsent: syncConsentState,
    onChange: syncConsentState,
  });
}
